/* eslint-disable no-console */
import { Component, HostBinding, Inject, Input, OnDestroy } from '@angular/core';
import { AppState } from '@app/core/state/appState';
import { fileNameNormalizer } from '@app/shared/misc/fileNameNormalizer';
import { Store } from '@ngrx/store';
import { SegmentedButtonProps } from '@bl/components';
import FileSaver from 'file-saver';
import { MenuItem } from 'primeng/api';
import { TableRowUnSelectEvent } from 'primeng/table';
import { Subject, takeUntil } from 'rxjs';
import { last } from 'rxjs/operators';
import { Client } from 'src/app/core/entity/client';
import { Document, RenameDocument } from 'src/app/core/entity/document';
import { DocumentService } from 'src/app/core/services/document.service';
import { ToastActions } from 'src/app/core/state/toast/toast.actions';
import { createPathState } from './file-browser-path.util';
import { FileBrowserUploadBuilderService } from './file-browser-upload-builder.service';
import { FileBrowserBusyState, FileSystemEntryLike, FileSystemTreeEntry } from './file-browser.models';

const MAX_FILE_SIZE_BYTES = 40_000_000;

type DocumentSection = 'internal' | 'shared';

@Component({
  selector: 'app-file-browser',
  templateUrl: `./file.browser.component.html`,
  providers: [DocumentService],
  styles: [
    `
      :host.section-internal-active ::ng-deep #internal,
      :host.section-shared-active ::ng-deep #shared {
        background-color: var(--action-subtle);
        border-color: var(--focus);
        color: var(--label);
        font-weight: 600;
      }
    `,
  ],
})
export class FileBrowserComponent implements OnDestroy {
  @Input()
  set active(value: boolean) {
    if (value === false) {
      return;
    }

    this.loadFiles();
  }
  @Input()
  set client(client: Client) {
    this.resetUploadPresentation();

    if (client && client.id !== -1) {
      this._client = client;
      return;
    }

    this.pathState.reset();
    this.files = [];
    this._client = null;
  }

  hidePleaseWait = true;
  displayDeleteFile = false;
  displayRenameDialog = false;
  loading = false;
  sendingFiles = false;
  directory = '';
  files: Document[] = [];
  selectedRow: Document;
  pendingDeletionDocument: Document = new Document();
  pendingRenameDocument: RenameDocument = new RenameDocument();
  _client: Client;
  contextMenuItems: MenuItem[];
  columns = [{ field: 'name', header: 'Filnamn', sortable: true }, { field: 'file' }];
  pathState = createPathState();
  activeSection: DocumentSection = 'internal';
  sectionButtons: SegmentedButtonProps['buttons'];

  private readonly destroy$ = new Subject<void>();
  private busyState: FileBrowserBusyState = 'idle';

  constructor(
    @Inject(Store) private readonly store: Store<AppState>,
    private readonly documentService: DocumentService,
    private readonly uploadBuilder: FileBrowserUploadBuilderService,
  ) {
    this.sectionButtons = [
      {
        id: 'internal',
        position: 'left',
        text: 'Interna dokument',
        onclick: () => this.setActiveSection('internal'),
      },
      {
        id: 'shared',
        position: 'right',
        text: 'Delade dokument',
        onclick: () => this.setActiveSection('shared'),
      },
    ];
    this.contextMenuItems = [
      {
        label: 'Öppna',
        icon: 'fa-folder-open',
        command: () => this.contextMenuOpen(),
      },
      {
        label: 'Byt namn',
        icon: 'fa-pencil-square',
        command: () => this.changeName(this.selectedRow),
      },
      {
        label: 'Ta bort',
        icon: 'fa-trash',
        command: () => this.deleteFile(this.selectedRow),
      },
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFiles() {
    if (!this.hasClient()) {
      return;
    }

    this.reloadFilesAtPath('');
  }

  setActiveSection(section: DocumentSection) {
    this.activeSection = section;
  }

  @HostBinding('class.section-internal-active') get isInternalActive() {
    return this.activeSection === 'internal';
  }

  @HostBinding('class.section-shared-active') get isSharedActive() {
    return this.activeSection === 'shared';
  }

  drop(event: DragEvent) {
    this.stopEvent(event);

    if (this.activeSection !== 'internal') {
      return;
    }

    if (this.sendingFiles) {
      this.store.dispatch(
        ToastActions.showWarnMessage({
          summary: 'Upptagen',
          detail: 'Sparar redan filer - var god försök igen senare',
        }),
      );
      return;
    }

    const entries = this.getDropEntries(event);
    if (entries.length === 0) {
      return;
    }

    this.setUploadBusyState();

    this.uploadBuilder
      .createZipUploadFileFromEntries(entries)
      .then((uploadFile) => {
        this.fileSaver([uploadFile]);
      })
      .catch((error: unknown) => {
        this.handleError(error);
      });
  }

  dragEnter(event: DragEvent) {
    this.stopEvent(event);
    if (event.dataTransfer) {
      event.dataTransfer.dropEffect = 'copy';
    }
  }

  dragOver(event: DragEvent) {
    this.stopEvent(event);
    if (event.dataTransfer) {
      event.dataTransfer.effectAllowed = 'copyMove';
    }
  }

  dragLeave(event: DragEvent) {
    this.stopEvent(event);
    if (this.busyState !== 'uploading') {
      this.hidePleaseWait = true;
    }
  }

  onRowUnselect(event: TableRowUnSelectEvent) {
    setTimeout(() => {
      this.selectedRow = event.data;
    }, 0);
  }

  onRowDblClick(selectedFile: Document) {
    this.selectedRow = selectedFile;

    if (!this.hasClient()) {
      return;
    }

    if (this.selectedRow.isFolder) {
      this.pathState.append(this.selectedRow.name);
      this.reloadFilesAtPath(this.pathState.currentRealPath);
      return;
    }

    const filePath = this.pathState.currentRealPath + selectedFile.name;

    this.documentService
      .getFile(this._client.id, filePath)
      .pipe(takeUntil(this.destroy$))
      .subscribe((file) => {
        FileSaver.saveAs(new Blob([file]), selectedFile.name);
      });
  }

  upDir() {
    this.pathState.up();
    this.reloadFilesAtPath(this.pathState.currentRealPath);
  }

  createDir() {
    if (!this.hasClient()) {
      return;
    }

    this.sendingFiles = true;
    this.busyState = 'uploading';

    this.documentService
      .createDirectory(this._client.id, this.pathState.currentRealPath, this.directory)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.reloadFilesAtPath(this.pathState.currentRealPath);
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: 'Skapad',
            detail: 'Mappen ' + fileNameNormalizer(this.directory) + ' har skapats',
          }),
        );
        this.directory = null;
        this.sendingFiles = false;
        this.busyState = 'idle';
      });
  }

  deleteFile(document: Document) {
    this.pendingDeletionDocument = document;
    this.displayDeleteFile = true;
  }

  afterDeleteDialog(proceedWithDelete: boolean) {
    this.displayDeleteFile = false;

    if (!proceedWithDelete) {
      this.pendingDeletionDocument = new Document();
      return;
    }

    if (!this.hasClient()) {
      this.pendingDeletionDocument = new Document();
      return;
    }

    this.hidePleaseWait = false;
    this.sendingFiles = true;
    this.busyState = 'uploading';

    const deletionPath =
      this.pathState.currentRealPath +
      this.pendingDeletionDocument.name +
      (this.pendingDeletionDocument.isFolder ? '/' : '');

    this.documentService
      .deleteFileOrFolder(this._client.id, deletionPath)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.reloadFilesAtPath(this.pathState.currentRealPath);
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: 'Borttaget',
            detail: 'Filen/mappen ' + this.pendingDeletionDocument.name + ' har tagits bort',
          }),
        );
        this.pendingDeletionDocument = new Document();
        this.resetUploadPresentation();
      });
  }

  afterRenameDialog(proceedWithRename: boolean) {
    this.displayRenameDialog = false;

    if (!proceedWithRename) {
      this.reloadFilesAtPath(this.pathState.currentRealPath);
      this.pendingRenameDocument = new RenameDocument();
      return;
    }

    if (!this.hasClient()) {
      this.pendingRenameDocument = new RenameDocument();
      return;
    }

    this.sendingFiles = true;
    this.busyState = 'uploading';

    const newName = fileNameNormalizer(this.pendingRenameDocument.newName);
    const currentPath =
      this.pathState.currentRealPath +
      this.pendingRenameDocument.name +
      (this.pendingRenameDocument.isFolder ? '/' : '');

    this.documentService
      .renameFileOrFolder(this._client.id, currentPath, newName)
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.store.dispatch(
          ToastActions.showInfoMessage({
            summary: 'Namnbyte',
            detail: 'Nytt namn har sparats: ' + fileNameNormalizer(this.pendingRenameDocument.newName),
          }),
        );
        this.reloadFilesAtPath(this.pathState.currentRealPath);
        this.pendingRenameDocument = new RenameDocument();
        this.sendingFiles = false;
        this.busyState = 'idle';
      });
  }

  // The file input lives in the internal-section child component, which emits the picked files.
  onUploadFiles(files: File[]) {
    if (files.length === 0) {
      return;
    }

    this.setUploadBusyState();

    this.uploadBuilder
      .createZipUploadFileFromPickerFiles(files)
      .then((uploadFile) => {
        this.fileSaver([uploadFile]);
      })
      .catch((error: unknown) => {
        this.handleError(error);
      });
  }

  private changeName(document: Document) {
    this.pendingRenameDocument = JSON.parse(JSON.stringify(document));
    this.pendingRenameDocument.newName = document.name;

    this.displayRenameDialog = true;
  }

  private handleError(error: unknown) {
    console.error('Fel vid uppladdning av fil', error);
    this.resetUploadPresentation();
    this.store.dispatch(
      ToastActions.showErrorMessage({
        summary: 'Ett fel har uppstått',
        detail: 'Tryck F5 för att ladda om applikationen. Om felet kvarstår, kontakta Support.',
      }),
    );
  }

  private contextMenuOpen() {
    this.onRowDblClick(this.selectedRow);
  }

  private reloadFilesAtPath(path: string) {
    if (!this.hasClient()) {
      return;
    }

    // const pathState = this.pathService.createPathState(path);
    this.loading = true;
    this.busyState = 'loading';

    this.documentService
      .getFilesAndFolders(this._client.id, path)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (docs) => {
          this.files = docs;
          this.selectedRow = null;
          this.loading = false;
          this.busyState = 'idle';
        },
        error: (error: unknown) => {
          this.loading = false;
          this.busyState = 'idle';
          this.handleError(error);
        },
      });
  }

  // eslint-disable-next-line max-lines-per-function
  private fileSaver(files: File[]) {
    if (files.length === 0 || !this.hasClient()) {
      return;
    }

    const [file, ...restFiles] = files;

    if (file.size > MAX_FILE_SIZE_BYTES) {
      this.resetUploadPresentation();
      this.store.dispatch(
        ToastActions.showWarnMessage({
          summary: 'Stor fil',
          detail: 'Filen är för stor. Prova att komprimera denna innan sparande eller försök med en mindre fil.',
        }),
      );
      return;
    }

    this.documentService
      .saveFile(this._client.id, this.pathState.currentRealPath, file)
      .pipe(takeUntil(this.destroy$), last())
      .subscribe({
        next: () => {
          if (restFiles.length > 0) {
            this.fileSaver(restFiles);
            return;
          }

          this.reloadFilesAtPath(this.pathState.currentRealPath);
          this.store.dispatch(
            ToastActions.showInfoMessage({
              summary: 'Sparat',
              detail: 'Filen/filerna har sparats i molnet',
            }),
          );
          this.resetUploadPresentation();
        },
        error: (err: unknown) => {
          console.error('File could not be saved', err);
          this.store.dispatch(
            ToastActions.showWarnMessage({
              summary: 'Filen kunde inte sparas',
              detail: 'Kanske är filen för stor eller så har ett annat fel uppståt.',
            }),
          );
          this.reloadFilesAtPath(this.pathState.currentRealPath);
          this.hidePleaseWait = true;
          this.sendingFiles = false;
          this.busyState = 'idle';
        },
      });
  }

  private setUploadBusyState() {
    this.hidePleaseWait = false;
    this.sendingFiles = true;
    this.busyState = 'uploading';
  }

  private resetUploadPresentation() {
    this.hidePleaseWait = true;
    this.sendingFiles = false;
    this.busyState = 'idle';
  }

  private stopEvent(event: Event) {
    event.preventDefault();
    event.stopPropagation();
  }

  private hasClient(): boolean {
    return !!this._client && this._client.id !== -1;
  }

  private getDropEntries(event: DragEvent): FileSystemTreeEntry[] {
    const items = Array.from(event.dataTransfer?.items ?? []);
    const entries = items
      .map((item) => (item as DataTransferItem & { webkitGetAsEntry?: () => unknown }).webkitGetAsEntry?.() as unknown)
      .filter((entry): entry is FileSystemEntryLike => !!entry);

    return entries as FileSystemTreeEntry[];
  }
}
